import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Book,
  Plus,
  Search,
  Edit2,
  Trash2,
  Check,
  X,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Save,
  Eye
} from 'react-feather';
import '../../../public/styles/eddy-shared.css';
import { HelpModal, HelpButton } from '../../components/HelpModal';
import { TeachingHelpContent } from '../../components/TeachingHelpContent';
import { TagInput } from '../../components/TagInput';
import { TagFilter } from '../../components/TagFilter';

// CSS for teaching content display
const teachingContentStyles = `
  .teaching-content-display h1,
  .teaching-content-display h2,
  .teaching-content-display h3,
  .teaching-content-display h4,
  .teaching-content-display h5,
  .teaching-content-display h6 {
    font-weight: 600;
    margin-top: 1.5rem;
    margin-bottom: 0.75rem;
    color: #374151;
  }

  .teaching-content-display h1 { font-size: 1.5rem; }
  .teaching-content-display h2 { 
    font-size: 1.25rem; 
    border-bottom: 2px solid #e5e7eb;
    padding-bottom: 0.5rem;
  }
  .teaching-content-display h3 { font-size: 1.125rem; }
  .teaching-content-display h4 { font-size: 1rem; }

  .teaching-content-display p {
    margin-bottom: 1rem;
    line-height: 1.6;
    color: #4b5563;
  }

  .teaching-content-display ul,
  .teaching-content-display ol {
    margin: 1rem 0;
    padding-left: 1.5rem;
  }

  .teaching-content-display li {
    margin-bottom: 0.5rem;
    line-height: 1.5;
  }

  .teaching-content-display strong {
    font-weight: 600;
    color: #374151;
  }

  .teaching-content-display em {
    font-style: italic;
  }

  .teaching-content-display blockquote {
    border-left: 4px solid #d1d5db;
    padding-left: 1rem;
    margin: 1rem 0;
    font-style: italic;
    color: #6b7280;
  }

  .teaching-content-display code {
    background-color: #f3f4f6;
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    font-family: 'Courier New', monospace;
    font-size: 0.875rem;
  }

  .teaching-content-display pre {
    background-color: #f3f4f6;
    padding: 1rem;
    border-radius: 0.5rem;
    overflow-x: auto;
    margin: 1rem 0;
  }

  .teaching-content-display hr {
    border: none;
    border-top: 1px solid #e5e7eb;
    margin: 2rem 0;
  }

  .teaching-content-display a {
    color: #5b86a9;
    text-decoration: underline;
  }

  .teaching-content-display a:hover {
    color: #4a6b7a;
  }

  .teaching-content-display ul {
    list-style-type: disc;
  }

  .teaching-content-display ol {
    list-style-type: decimal;
  }
`;

interface Teaching {
  id: string;
  query: string;
  content: string;
  audience: 'internal' | 'customer';
  priority: 'critical' | 'high' | 'medium' | 'low';
  type: 'correction' | 'new_content' | 'clarification' | 'update';
  status: 'pending' | 'approved' | 'rejected' | 'auto_approved';
  contributor?: string;
  createdAt: string;
  updatedAt: string;
  usageCount?: number;
  lastUsed?: string;
  tags?: string[];
  metadata?: {
    originalQuestion?: string;
    originalAnswer?: string;
    contextChunks?: any[];
  };
}

interface ContentSource {
  id: string;
  name: string;
  enabled: boolean;
}

const Teaching: React.FC = () => {
  const { user } = useAuth();
  const [teachings, setTeachings] = useState<Teaching[]>([]);
  const [filteredTeachings, setFilteredTeachings] = useState<Teaching[]>([]);
  const [_contentSources, setContentSources] = useState<ContentSource[]>([]);
  void _contentSources; // Suppress unused warning - data loaded for future filtering
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Check if user can use teaching features (Admin or Owner)
  const canUseTeaching = user?.capabilities?.canUseTeaching === true;
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSource, setSelectedSource] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedPriority, setSelectedPriority] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [selectedTeaching, setSelectedTeaching] = useState<Teaching | null>(null);

  // Form states
  const [formData, setFormData] = useState({
    query: '',
    content: '',
    audience: 'customer',
    priority: 'medium' as Teaching['priority'],
    type: 'correction' as Teaching['type'],
    contributor: '',
    tags: [] as string[]
  });

  // Expanded rows for details
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  
  // Preview toggle for modals
  const [showEditPreview, setShowEditPreview] = useState(false);
  const [showCreatePreview, setShowCreatePreview] = useState(false);

  // Utility functions
  const showNotification = (message: string, type: 'success' | 'error') => {
    if (type === 'success') {
      setSuccess(message);
      setTimeout(() => setSuccess(null), 3000);
    } else {
      setError(message);
      setTimeout(() => setError(null), 5000);
    }
  };

  const stripHtml = (html: string): string => {
    return html.replace(/<[^>]*>/g, '');
  };

  const _escapeHtml = (text: string): string => {
    const map: { [key: string]: string } = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    return text.replace(/[&<>"']/g, m => map[m]);
  };
  void _escapeHtml; // Suppress unused warning - utility kept for XSS prevention

  // Convert markdown to HTML for display
  const markdownToHtml = (markdown: string): string => {
    let html = markdown;
    
    // Headers
    html = html.replace(/^### (.*$)/gm, '<h3>$1</h3>');
    html = html.replace(/^## (.*$)/gm, '<h2>$1</h2>');
    html = html.replace(/^# (.*$)/gm, '<h1>$1</h1>');
    
    // Bold text (process first to avoid conflicts with italic)
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Italic text (simple version - will be processed after bold is already converted)
    html = html.replace(/\*([^*<>]+)\*/g, '<em>$1</em>');
    
    // Links
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
    
    // Numbered lists
    html = html.replace(/^(\d+)\. (.*$)/gm, '<li>$2</li>');
    
    // Unordered lists
    html = html.replace(/^- (.*$)/gm, '<li>$1</li>');
    
    // Code blocks (inline)
    html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
    
    // Convert double line breaks to paragraph breaks
    html = html.replace(/\n\n/g, '||PARAGRAPH||');
    
    // Convert single line breaks to <br>
    html = html.replace(/\n/g, '<br>');
    
    // Convert paragraph breaks back
    html = html.replace(/\|\|PARAGRAPH\|\|/g, '</p><p>');
    
    // Wrap consecutive list items in ul/ol tags
    html = html.replace(/(<li>.*?<\/li>)(\s*<br>\s*<li>.*?<\/li>)*/gs, (match) => {
      const isNumbered = /^\d+\./.test(markdown.match(/^(\d+\. .*$)/m)?.[0] || '');
      const tag = isNumbered ? 'ol' : 'ul';
      return `<${tag}>${match.replace(/<br>\s*/g, '')}</${tag}>`;
    });
    
    // Wrap content in paragraphs
    html = '<p>' + html + '</p>';
    
    // Clean up empty paragraphs and fix header/list wrapping
    html = html.replace(/<p><\/p>/g, '');
    html = html.replace(/<p>(<[h123]>)/g, '$1');
    html = html.replace(/(<\/[h123]>)<\/p>/g, '$1');
    html = html.replace(/<p>(<[uo]l>)/g, '$1');
    html = html.replace(/(<\/[uo]l>)<\/p>/g, '$1');
    
    return html;
  };

  // Load data functions
  const loadTeachings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/teaching?audience=${selectedSource}`);
      const data = await response.json();
      
      if (data.success && data.data && data.data.teachings) {
        setTeachings(data.data.teachings);
      } else {
        console.warn('No teachings found, using empty array');
        setTeachings([]);
        if (data.error) {
          showNotification(data.error, 'error');
        }
      }
    } catch (error) {
      console.error('Failed to load teachings:', error);
      showNotification('Failed to load teachings', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedSource]);

  const loadContentSources = useCallback(async () => {
    try {
      const response = await fetch(`/api/teaching/metadata/content-sources`);
      const data = await response.json();

      if (data.success && data.data && data.data.contentSources) {
        setContentSources(data.data.contentSources);
      } else {
        console.warn('No content sources found, using empty array');
        setContentSources([]);
      }
    } catch (error) {
      console.error('Failed to load content sources:', error);
      setContentSources([]);
    }
  }, []);

  const loadAvailableTags = useCallback(async () => {
    try {
      const response = await fetch(`/api/teaching/tags`);
      const data = await response.json();

      if (data.success && data.data && data.data.tags) {
        setAvailableTags(data.data.tags);
      } else {
        console.warn('No tags found, using empty array');
        setAvailableTags([]);
      }
    } catch (error) {
      console.error('Failed to load tags:', error);
      setAvailableTags([]);
    }
  }, []);

  // Filter teachings based on search and filters
  useEffect(() => {
    let filtered = teachings;
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t => 
        t.query.toLowerCase().includes(query) ||
        t.content.toLowerCase().includes(query)
      );
    }
    
    // Status filter
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(t => t.status === selectedStatus);
    }
    
    // Priority filter
    if (selectedPriority !== 'all') {
      filtered = filtered.filter(t => t.priority === selectedPriority);
    }
    
    // Type filter
    if (selectedType !== 'all') {
      filtered = filtered.filter(t => t.type === selectedType);
    }

    // Tag filter (OR logic - show teachings with any selected tag)
    if (selectedTags.length > 0) {
      filtered = filtered.filter(t =>
        t.tags && selectedTags.some(tag => t.tags!.includes(tag))
      );
    }

    setFilteredTeachings(filtered);
  }, [teachings, searchQuery, selectedStatus, selectedPriority, selectedType, selectedTags]);

  // Inject CSS styles for teaching content
  useEffect(() => {
    const styleId = 'teaching-content-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = teachingContentStyles;
      document.head.appendChild(style);
    }
  }, []);

  // Load initial data
  useEffect(() => {
    loadTeachings();
    loadContentSources();
    loadAvailableTags();
  }, [loadTeachings, loadContentSources, loadAvailableTags]);

  // CRUD operations
  const createTeaching = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`/api/teaching`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        showNotification('Teaching created successfully', 'success');
        setShowCreateModal(false);
        loadTeachings();
        // Reset form
        setFormData({
          query: '',
          content: '',
          audience: 'customer',
          priority: 'medium',
          type: 'correction',
          contributor: '',
          tags: []
        });
        setShowCreatePreview(false);
      } else {
        showNotification(data.error || 'Failed to create teaching', 'error');
      }
    } catch (error) {
      console.error('Failed to create teaching:', error);
      showNotification('Failed to create teaching', 'error');
    }
  };

  const updateTeaching = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedTeaching) return;
    
    try {
      const response = await fetch(`/api/teaching/${selectedTeaching.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: formData.query,
          content: formData.content,
          audience: formData.audience,
          priority: formData.priority,
          type: formData.type
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        showNotification('Teaching updated successfully', 'success');
        setShowEditModal(false);
        loadTeachings();
      } else {
        showNotification(data.error || 'Failed to update teaching', 'error');
      }
    } catch (error) {
      console.error('Failed to update teaching:', error);
      showNotification('Failed to update teaching', 'error');
    }
  };

  const deleteTeaching = async (id: string) => {
    if (!confirm('Are you sure you want to delete this teaching? This will also remove it from the vector store.')) {
      return;
    }
    
    try {
      const response = await fetch(`/api/teaching/${id}?confirm=true`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.success) {
        showNotification('Teaching deleted successfully', 'success');
        loadTeachings();
      } else {
        showNotification(data.error || 'Failed to delete teaching', 'error');
      }
    } catch (error) {
      console.error('Failed to delete teaching:', error);
      showNotification('Failed to delete teaching', 'error');
    }
  };

  const updateTeachingStatus = async (id: string, status: 'approved' | 'rejected') => {
    try {
      const response = await fetch(`/api/teaching/${id}/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      
      const data = await response.json();
      
      if (data.success) {
        showNotification(`Teaching ${status}`, 'success');
        loadTeachings();
        setShowDetailModal(false);
      } else {
        showNotification(data.error || 'Failed to update teaching status', 'error');
      }
    } catch (error) {
      console.error('Failed to update teaching status:', error);
      showNotification('Failed to update teaching status', 'error');
    }
  };

  // UI helper functions
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-eddy-orange/10 text-eddy-orange border-eddy-orange/20';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-eddy-green/10 text-eddy-green border-eddy-green/20';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'approved': return 'bg-eddy-green/10 text-eddy-green border-eddy-green/20';
      case 'rejected': return 'bg-red-100 text-red-800 border-red-200';
      case 'auto_approved': return 'bg-eddy-blue/10 text-eddy-blue border-eddy-blue/20';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'correction': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'new_content': return 'bg-eddy-blue/10 text-eddy-blue border-eddy-blue/20';
      case 'clarification': return 'bg-cyan-100 text-cyan-800 border-cyan-200';
      case 'update': return 'bg-teal-100 text-teal-800 border-teal-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const toggleRow = (id: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const openEditModal = (teaching: Teaching) => {
    setSelectedTeaching(teaching);
    setFormData({
      query: stripHtml(teaching.query),
      content: stripHtml(teaching.content),
      audience: teaching.audience,
      priority: teaching.priority,
      type: teaching.type,
      contributor: teaching.contributor || '',
      tags: teaching.tags || []
    });
    setShowEditPreview(false);
    setShowEditModal(true);
  };

  const openDetailModal = async (teaching: Teaching) => {
    try {
      const response = await fetch(`/api/teaching/${teaching.id}`);
      const data = await response.json();
      
      if (data.success) {
        setSelectedTeaching(data.data.teaching);
        setShowDetailModal(true);
      }
    } catch (error) {
      console.error('Failed to load teaching details:', error);
      showNotification('Failed to load teaching details', 'error');
    }
  };

  return (
    <div className="space-y-6">
      {/* Notifications */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center">
          <AlertCircle size={16} className="mr-2" />
          {error}
        </div>
      )}
      
      {success && (
        <div className="bg-eddy-green/10 border border-eddy-green/20 text-eddy-green px-4 py-3 rounded-lg flex items-center">
          <Check size={16} className="mr-2" />
          {success}
        </div>
      )}

      {/* Header Section */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-semibold text-eddy-primary flex items-center">
              <Book size={24} className="mr-2" />
              Teaching System Management
            </h2>
            <p className="mt-2 text-gray-600">
              Manage corrections, clarifications, and custom knowledge
            </p>
          </div>
          
          <div className="mt-4 md:mt-0 flex gap-2 items-center">
            {canUseTeaching && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-eddy-blue text-white px-4 py-2 rounded-lg hover:bg-eddy-blue/90 transition-colors flex items-center"
              >
                <Plus size={16} className="mr-2" />
                Create Teaching
              </button>
            )}
            <HelpButton onClick={() => setShowHelpModal(true)} />
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Search teachings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-eddy-blue focus:border-transparent"
            />
          </div>
          
          <select
            value={selectedSource}
            onChange={(e) => setSelectedSource(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-eddy-blue focus:border-transparent"
          >
            <option value="all">All Audiences</option>
            <option value="customer">Customer</option>
            <option value="internal">Internal</option>
          </select>
          
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-eddy-blue focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="auto_approved">Auto Approved</option>
          </select>
          
          <select
            value={selectedPriority}
            onChange={(e) => setSelectedPriority(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-eddy-blue focus:border-transparent"
          >
            <option value="all">All Priorities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-eddy-blue focus:border-transparent"
          >
            <option value="all">All Types</option>
            <option value="correction">Correction</option>
            <option value="new_content">New Content</option>
            <option value="clarification">Clarification</option>
            <option value="update">Update</option>
          </select>

          <TagFilter
            selectedTags={selectedTags}
            onTagsChange={setSelectedTags}
            availableTags={availableTags}
          />
        </div>
      </div>

      {/* Teaching List */}
      <div className="bg-white rounded-lg shadow-sm">
        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw className="animate-spin mx-auto mb-4 text-eddy-blue" size={32} />
            <p className="text-gray-600">Loading teachings...</p>
          </div>
        ) : filteredTeachings.length === 0 ? (
          <div className="p-8 text-center">
            <Book className="mx-auto mb-4 text-gray-400" size={48} />
            <p className="text-gray-600 mb-4">No teachings found</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-eddy-blue text-white px-4 py-2 rounded-lg hover:bg-eddy-blue/90 transition-colors"
            >
              Create First Teaching
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {(filteredTeachings || []).map((teaching) => (
              <div 
                key={teaching.id} 
                className="hover:bg-gray-50 hover:shadow-sm transition-all duration-200 cursor-pointer"
                onClick={() => openDetailModal(teaching)}
              >
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start space-x-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRow(teaching.id);
                          }}
                          className="mt-1 text-gray-400 hover:text-gray-600"
                        >
                          {expandedRows.has(teaching.id) ? 
                            <ChevronUp size={20} /> : 
                            <ChevronDown size={20} />
                          }
                        </button>
                        
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-medium text-gray-900 truncate">
                            {teaching.query}
                          </h3>
                          
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getPriorityColor(teaching.priority)}`}>
                              {teaching.priority}
                            </span>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(teaching.status)}`}>
                              {teaching.status.replace('_', ' ')}
                            </span>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getTypeColor(teaching.type)}`}>
                              {teaching.type.replace('_', ' ')}
                            </span>
                            {teaching.tags && teaching.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1.5">
                                {teaching.tags.map(tag => (
                                  <span key={tag} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-eddy-blue/10 text-eddy-blue border border-eddy-blue/20">
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                            {teaching.usageCount !== undefined && (
                              <span className="text-xs text-gray-500">
                                Used {teaching.usageCount} times
                              </span>
                            )}
                          </div>
                          
                          <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                            {stripHtml(teaching.content).substring(0, 150)}
                            {stripHtml(teaching.content).length > 150 ? '...' : ''}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="ml-4 flex items-center space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openDetailModal(teaching);
                        }}
                        className="p-2 text-gray-400 hover:text-eddy-blue hover:bg-eddy-blue/10 rounded-lg transition-colors"
                        title="View Details"
                      >
                        <Eye size={16} />
                      </button>
                      {canUseTeaching && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditModal(teaching);
                          }}
                          className="p-2 text-gray-400 hover:text-eddy-blue hover:bg-eddy-blue/10 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </button>
                      )}
                      {canUseTeaching && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteTeaching(teaching.id);
                          }}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  {expandedRows.has(teaching.id) && (
                    <div className="mt-4 ml-8 p-4 bg-gray-50 rounded-lg">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Audience</p>
                          <p className="font-medium">{teaching.audience}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Contributor</p>
                          <p className="font-medium">{teaching.contributor || 'System'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Created</p>
                          <p className="font-medium">{new Date(teaching.createdAt).toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Last Updated</p>
                          <p className="font-medium">{new Date(teaching.updatedAt).toLocaleString()}</p>
                        </div>
                      </div>
                      
                      {teaching.metadata?.originalQuestion && (
                        <div className="mt-4">
                          <p className="text-gray-500 text-sm">Original Context</p>
                          <div className="mt-2 p-3 bg-white rounded border border-gray-200">
                            <p className="text-sm font-medium text-gray-700">Question:</p>
                            <p className="text-sm text-gray-600">{teaching.metadata.originalQuestion}</p>
                            {teaching.metadata.originalAnswer && (
                              <>
                                <p className="text-sm font-medium text-gray-700 mt-2">Previous Answer:</p>
                                <p className="text-sm text-gray-600">{teaching.metadata.originalAnswer.substring(0, 200)}...</p>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setShowCreateModal(false)}
        >
          <div 
            className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-eddy-blue text-white p-6 rounded-t-lg border-b border-eddy-blue/20 flex items-center justify-between">
              <h3 className="text-xl font-semibold">Create New Teaching</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                title="Close modal"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
            
            <form onSubmit={createTeaching} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Query/Question
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.query}
                    onChange={(e) => setFormData({ ...formData, query: e.target.value })}
                    placeholder="What question does this teaching answer?"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-eddy-blue focus:border-transparent"
                  />
                </div>
                
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Teaching Content
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowCreatePreview(!showCreatePreview)}
                      className="text-sm text-eddy-blue hover:text-eddy-blue/80 underline transition-colors flex items-center"
                    >
                      <Eye size={14} className="mr-1" />
                      {showCreatePreview ? 'Hide Preview' : 'Show Live Preview'}
                    </button>
                  </div>
                  
                  <textarea
                    required
                    rows={showCreatePreview ? 8 : 12}
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="The answer or information to teach..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-eddy-blue focus:border-transparent text-sm font-mono"
                  />
                  
                  {showCreatePreview && (
                    <div className="mt-4">
                      <label className="block text-xs text-gray-500 mb-2">Live Preview</label>
                      <div className="p-4 border border-gray-300 rounded-lg bg-gray-50 max-h-80 overflow-y-auto">
                        <div className="teaching-content-display text-sm" 
                             dangerouslySetInnerHTML={{ __html: markdownToHtml(formData.content || '') }} />
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Audience
                    </label>
                    <select
                      value={formData.audience}
                      onChange={(e) => setFormData({ ...formData, audience: e.target.value as 'internal' | 'customer' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-eddy-blue focus:border-transparent"
                    >
                      <option value="customer">Customer</option>
                      <option value="internal">Internal</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value as Teaching['type'] })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-eddy-blue focus:border-transparent"
                    >
                      <option value="correction">Correction</option>
                      <option value="new_content">New Content</option>
                      <option value="clarification">Clarification</option>
                      <option value="update">Update</option>
                    </select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priority
                    </label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value as Teaching['priority'] })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-eddy-blue focus:border-transparent"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contributor (Optional)
                    </label>
                    <input
                      type="text"
                      value={formData.contributor}
                      onChange={(e) => setFormData({ ...formData, contributor: e.target.value })}
                      placeholder="Name or identifier"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-eddy-blue focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags (Optional)
                  </label>
                  <TagInput
                    tags={formData.tags}
                    onTagsChange={(tags) => setFormData({ ...formData, tags })}
                    availableTags={availableTags}
                    placeholder="Add tags to organize this teaching"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-eddy-blue text-white rounded-lg hover:bg-eddy-blue/90 transition-colors flex items-center"
                >
                  <Plus size={16} className="mr-2" />
                  Create Teaching
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedTeaching && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setShowEditModal(false)}
        >
          <div 
            className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-eddy-green text-white p-6 rounded-t-lg border-b border-eddy-green/20 flex items-center justify-between">
              <h3 className="text-xl font-semibold">Edit Teaching</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                title="Close modal"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
            
            <form onSubmit={updateTeaching} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Query/Question
                  </label>
                  <textarea
                    required
                    rows={3}
                    value={formData.query}
                    onChange={(e) => setFormData({ ...formData, query: e.target.value })}
                    placeholder="What question does this teaching answer?"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-eddy-blue focus:border-transparent"
                  />
                </div>
                
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Teaching Content
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowEditPreview(!showEditPreview)}
                      className="text-sm text-eddy-blue hover:text-eddy-blue/80 underline transition-colors flex items-center"
                    >
                      <Eye size={14} className="mr-1" />
                      {showEditPreview ? 'Hide Preview' : 'Show Live Preview'}
                    </button>
                  </div>
                  
                  <textarea
                    required
                    rows={showEditPreview ? 8 : 12}
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="The answer or information to teach..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-eddy-blue focus:border-transparent text-sm font-mono"
                  />
                  
                  {showEditPreview && (
                    <div className="mt-4">
                      <label className="block text-xs text-gray-500 mb-2">Live Preview</label>
                      <div className="p-4 border border-gray-300 rounded-lg bg-gray-50 max-h-80 overflow-y-auto">
                        <div className="teaching-content-display text-sm" 
                             dangerouslySetInnerHTML={{ __html: markdownToHtml(formData.content || '') }} />
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Audience
                    </label>
                    <select
                      value={formData.audience}
                      onChange={(e) => setFormData({ ...formData, audience: e.target.value as 'internal' | 'customer' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-eddy-blue focus:border-transparent"
                    >
                      <option value="customer">Customer</option>
                      <option value="internal">Internal</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priority
                    </label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value as Teaching['priority'] })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-eddy-blue focus:border-transparent"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value as Teaching['type'] })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-eddy-blue focus:border-transparent"
                    >
                      <option value="correction">Correction</option>
                      <option value="new_content">New Content</option>
                      <option value="clarification">Clarification</option>
                      <option value="update">Update</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags (Optional)
                  </label>
                  <TagInput
                    tags={formData.tags}
                    onTagsChange={(tags) => setFormData({ ...formData, tags })}
                    availableTags={availableTags}
                    placeholder="Add tags to organize this teaching"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-eddy-blue text-white rounded-lg hover:bg-eddy-blue/90 transition-colors flex items-center"
                >
                  <Save size={16} className="mr-2" />
                  Save Changes
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedTeaching && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setShowDetailModal(false)}
        >
          <div 
            className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-eddy-primary text-white p-6 rounded-t-lg border-b border-eddy-primary/20 flex items-center justify-between">
              <h3 className="text-xl font-semibold">Teaching Details</h3>
              <button
                onClick={() => setShowDetailModal(false)}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                title="Close modal"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
            
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Query/Question</h4>
                  <p className="mt-1 text-gray-900">{selectedTeaching.query}</p>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Teaching Content</h4>
                  <div className="mt-1 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="teaching-content-display" 
                         dangerouslySetInnerHTML={{ __html: markdownToHtml(selectedTeaching.content) }} />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Status</h4>
                    <span className={`mt-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(selectedTeaching.status)}`}>
                      {selectedTeaching.status.replace('_', ' ')}
                    </span>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Priority</h4>
                    <span className={`mt-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getPriorityColor(selectedTeaching.priority)}`}>
                      {selectedTeaching.priority}
                    </span>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Type</h4>
                    <span className={`mt-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getTypeColor(selectedTeaching.type)}`}>
                      {selectedTeaching.type.replace('_', ' ')}
                    </span>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Audience</h4>
                    <p className="mt-1">{selectedTeaching.audience}</p>
                  </div>
                </div>

                {selectedTeaching.tags && selectedTeaching.tags.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Tags</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedTeaching.tags.map(tag => (
                        <span key={tag} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-eddy-blue/10 text-eddy-blue border border-eddy-blue/20">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {selectedTeaching.metadata && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Context Information</h4>
                    <div className="mt-2 p-4 bg-gray-50 rounded-lg">
                      {selectedTeaching.metadata.originalQuestion && (
                        <div className="mb-3">
                          <p className="text-sm font-medium text-gray-700">Original Question:</p>
                          <p className="text-sm text-gray-600">{selectedTeaching.metadata.originalQuestion}</p>
                        </div>
                      )}
                      {selectedTeaching.metadata.originalAnswer && (
                        <div>
                          <p className="text-sm font-medium text-gray-700">Previous Answer:</p>
                          <p className="text-sm text-gray-600">{selectedTeaching.metadata.originalAnswer}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <h4 className="font-medium text-gray-500">Contributor</h4>
                    <p className="mt-1">{selectedTeaching.contributor || 'System'}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-500">Usage Count</h4>
                    <p className="mt-1">{selectedTeaching.usageCount || 0} times</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-500">Created</h4>
                    <p className="mt-1">{new Date(selectedTeaching.createdAt).toLocaleString()}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-500">Last Updated</h4>
                    <p className="mt-1">{new Date(selectedTeaching.updatedAt).toLocaleString()}</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 flex justify-end space-x-3">
                {selectedTeaching.status === 'pending' && (
                  <>
                    <button
                      onClick={() => updateTeachingStatus(selectedTeaching.id, 'rejected')}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center"
                    >
                      <X size={16} className="mr-2" />
                      Reject
                    </button>
                    <button
                      onClick={() => updateTeachingStatus(selectedTeaching.id, 'approved')}
                      className="px-4 py-2 bg-eddy-green text-white rounded-lg hover:bg-eddy-green/90 transition-colors flex items-center"
                    >
                      <Check size={16} className="mr-2" />
                      Approve
                    </button>
                  </>
                )}
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Help Modal */}
      <HelpModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        title="Teaching System Guide"
      >
        <TeachingHelpContent />
      </HelpModal>
    </div>
  );
};

export default Teaching;